import { useEffect, useState } from "react";
import { usePortal, type ClientDetail, type ClientSettingsImpact, type ClientSettingsInput, type ClientSettingsOptions } from "@app/portal-sdk";
import { Alert, AppBadge, Box, Button, DataTable, FormControlLabel, MenuItem, SectionCard, Stack, Switch, TextField, Typography } from "@app/ui-kit";
import { LoadingScreen } from "../../components/LoadingScreen";
import { formatDay } from "../../lib/format";
import { errorMessage, useApiQuery } from "../../lib/useApiQuery";

/**
 * REF-7: the client's settings, read from and written to portal.client. The
 * fiscal calendar, the currency, the locale, the time zone, the date display
 * and the confirmed flag. The ADM-5 impact preview is kept: a change of start
 * month states what it would move before the save is allowed. The four fiscal
 * options come from Channel Analytics' vocabulary until REF-11 moves them to
 * the portal; a client without that application sees its current values and
 * a sentence saying why the pickers are not offered yet.
 *
 * SET-1: the same controls render under the client's own settings section
 * (Regional and fiscal) and in the admin console's overview tab. Moved here,
 * not rebuilt; the title is the caller's.
 */
type Props = { client: ClientDetail; onSaved: (client: ClientDetail) => void; eyebrow?: string; title?: string; /** The admin console's route or the client's own (SET-1). */ base?: string };

export const ClientSettingsSection = ({ client, onSaved, eyebrow = "Client", title = "Settings", base = `/admin/clients/${client.id}/settings` }: Props) => {
  const { api } = usePortal();
  const meta = useApiQuery<ClientSettingsOptions>(() => api.request<ClientSettingsOptions>(base), [api, base]);
  const [form, setForm] = useState<ClientSettingsInput>({ ...client.settings });
  const [impact, setImpact] = useState<ClientSettingsImpact | null>(null);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string[] | null>(null);

  useEffect(() => { setForm({ ...client.settings }); }, [client.settings]);

  // The preview and the impact come from the database, on every fiscal change, debounced.
  useEffect(() => {
    const handle = setTimeout(() => {
      void api.request<ClientSettingsImpact>(`${base}/impact`, { method: "POST", json: form }).then(setImpact).catch((e) => setError(errorMessage(e)));
    }, 250);
    return () => clearTimeout(handle);
  }, [api, base, form.fiscalYearStartMonth, form.fiscalYearLabelBasis, form.fiscalYearLabelPrefix, form.fiscalYearLabelDigits]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = (domain: string) => meta.data?.options[domain] ?? [];
  const hasPickers = Boolean(meta.data && Object.keys(meta.data.options).length > 0);
  const moves = Boolean(impact?.impact?.moves);
  const startChanged = form.fiscalYearStartMonth !== client.settings.fiscalYearStartMonth;
  const canSave = reason.trim().length >= 3 && (!moves || confirm.trim() === client.slug) && !saving;
  const set = (patch: Partial<ClientSettingsInput>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const r = await api.request<{ client: ClientDetail; changed: string[] }>(base, { method: "PUT", json: { ...form, reason: reason.trim(), confirm: moves ? confirm.trim() : null } });
      setSaved(r.changed); setReason(""); setConfirm(""); setImpact(null);
      onSaved(r.client);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (meta.loading && !meta.data) return <SectionCard eyebrow={eyebrow} title={title}><LoadingScreen minHeight={120} /></SectionCard>;
  const preview = impact?.preview ?? null;
  const first = preview?.years[0];
  const imp = impact?.impact ?? null;
  const select = (domain: string, label: string, value: string, onChange: (v: string) => void, width = 200) =>
    hasPickers ? (
      <TextField select label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: width }} helperText={options(domain).find((o) => o.code === value)?.description ?? " "}>
        {options(domain).map((o) => <MenuItem key={o.code} value={o.code}>{o.label}</MenuItem>)}
      </TextField>
    ) : (
      <TextField label={label} value={value} disabled sx={{ minWidth: width }} helperText=" " />
    );

  return (
    <SectionCard eyebrow={eyebrow} title={title}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>
            Settings that are true of your company whichever application is asking, held on your own row.
          </Typography>
          <AppBadge label={client.settings.fiscalCalendarConfirmed ? "Fiscal year confirmed" : "Fiscal year unconfirmed"} size="small" color={client.settings.fiscalCalendarConfirmed ? "success" : "warning"} variant={client.settings.fiscalCalendarConfirmed ? "filled" : "outlined"} />
        </Stack>
        {!client.settings.fiscalCalendarConfirmed && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 820 }}>
            You are on the calendar-year default and nobody has confirmed it. Every fiscal label in your applications shows as provisional until a person saves here with the confirmed switch on.
          </Typography>
        )}
        {meta.error && <Alert severity="error">{meta.error}</Alert>}
        {!hasPickers && meta.data && (
          <Alert severity="info">{meta.data.note ?? "The fiscal options are not available for this client yet, so the fiscal fields are shown as they are."}</Alert>
        )}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {saved && <Alert severity="success" onClose={() => setSaved(null)}>{saved.length ? `Saved. Changed: ${saved.join(", ")}. The change is in your activity log.` : "Saved. Nothing changed, so nothing was logged."}</Alert>}

        <Typography variant="subtitle2">Fiscal calendar</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
          {select("fiscal_start_month", "Fiscal year starts in", String(form.fiscalYearStartMonth), (v) => set({ fiscalYearStartMonth: Number(v) }))}
          {select("fiscal_year_label_basis", "Named after the year it", form.fiscalYearLabelBasis, (v) => set({ fiscalYearLabelBasis: v }), 220)}
          {select("fiscal_year_label_prefix", "Prefix", form.fiscalYearLabelPrefix, (v) => set({ fiscalYearLabelPrefix: v }), 120)}
          {select("fiscal_year_label_digits", "Digits", String(form.fiscalYearLabelDigits), (v) => set({ fiscalYearLabelDigits: Number(v) }), 160)}
        </Stack>
        {preview && first && (
          <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: "6px" }}>
            <Typography variant="body1">
              The fiscal year beginning <strong>{formatDay(first.yearStart, "long")}</strong> and ending <strong>{formatDay(first.yearEnd, "long")}</strong> will be shown as <strong>{first.label}</strong>. Today falls in <strong>{preview.today.quarterLabel}</strong>.
            </Typography>
          </Box>
        )}
        {imp && imp.moves && (
          <Stack spacing={1.5}>
            <Alert severity="warning">
              Changing the start month rewrites {imp.calendarRows.toLocaleString()} calendar rows. {imp.quarterChanges.length} initiative{imp.quarterChanges.length === 1 ? "" : "s"} would change fiscal quarter and {imp.yearChanges.length} would change fiscal year, which moves {imp.yearChanges.length === 1 ? "it" : "them"} between operating plans.
              {imp.valueMismatches.length > 0 && ` ${imp.valueMismatches.length} capability value${imp.valueMismatches.length === 1 ? "" : "s"} would sit in a fiscal year whose boundaries no longer match the initiatives against that capability.`}
            </Alert>
            {imp.quarterChanges.length > 0 && (
              <DataTable
                columns={[{ key: "init", label: "Initiative" }, { key: "dates", label: "Dates" }, { key: "from", label: "Now" }, { key: "to", label: "After" }, { key: "year", label: "" }]}
                rows={imp.quarterChanges.map((m) => ({
                  init: <><Typography variant="body2">{m.initiativeName}</Typography><Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>{m.initiativeCode}</Typography></>,
                  dates: `${formatDay(m.startsOn)} to ${formatDay(m.endsOn)}`,
                  from: m.from, to: m.to,
                  year: imp.yearChanges.some((y) => y.initiativeCode === m.initiativeCode) ? <AppBadge label="changes year" size="small" color="warning" /> : ""
                }))}
              />
            )}
            <TextField label={`Type the portal address ${client.slug} to confirm`} value={confirm} onChange={(e) => setConfirm(e.target.value)} size="small" placeholder={client.slug} sx={{ maxWidth: 420 }} />
          </Stack>
        )}
        {imp && !imp.moves && startChanged && <Alert severity="info">{impact?.note ?? "Nothing dated moves: there are no initiatives with dates yet. The calendar is still rewritten."}</Alert>}
        {!imp && startChanged && impact?.note && <Alert severity="info">{impact.note}</Alert>}
        <FormControlLabel control={<Switch checked={form.fiscalCalendarConfirmed} onChange={(e) => set({ fiscalCalendarConfirmed: e.target.checked })} />} label="Confirmed: a person has decided this is your fiscal year" />

        <Typography variant="subtitle2">Currency, locale and display</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} flexWrap="wrap" useFlexGap>
          <TextField label="Currency" value={form.baseCurrency} onChange={(e) => set({ baseCurrency: e.target.value.toUpperCase() })} inputProps={{ maxLength: 3 }} sx={{ width: 120 }} helperText="ISO 4217" />
          <TextField label="Locale" value={form.locale} onChange={(e) => set({ locale: e.target.value })} sx={{ width: 140 }} helperText="e.g. en-US" />
          <TextField label="Time zone" value={form.timeZone} onChange={(e) => set({ timeZone: e.target.value })} sx={{ width: 220 }} helperText="IANA, e.g. America/New_York" />
          <TextField label="Date display" value={form.dateDisplay} onChange={(e) => set({ dateDisplay: e.target.value })} sx={{ width: 120 }} helperText="us or iso" />
        </Stack>

        <TextField label="Reason" helperText="Recorded in your activity log with your name. Required." value={reason} onChange={(e) => setReason(e.target.value)} fullWidth multiline minRows={2} sx={{ maxWidth: 820 }} />
        <Box>
          <Button variant="contained" onClick={() => void save()} disabled={!canSave}>{moves ? "Confirm and save" : "Save settings"}</Button>
        </Box>
      </Stack>
    </SectionCard>
  );
};
